package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.EntityPreview
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge

/**
 * 实体预览渲染器（Stage G4 骨架）。
 *
 * ui-runtime 层只调 [UiRenderBridge.drawEntityPreview] 委托平台实现；具体 3D 渲染 / 朝向跟随鼠标 /
 * source 解析（self / uuid / mob entity）由 fabric / bukkit 的 UiRenderBridge 实现类完成。
 *
 * visible=false 由 UIRenderer 统一在递归入口拦截，本类不再重复判断。
 *
 * UiRenderBridge.drawEntityPreview 不存在时走 default noop（见 UiRenderBridge 接口默认方法）；
 * 意味着无平台支持时 entityPreview 元素实际不显示但不报错，兼容未升级的旧客户端。
 */
@Suppress("MagicNumber")
class EntityPreviewRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val preview = element as? EntityPreview ?: return

        // Task 2a：背景填充（在 3D 实体之前合成）。null 时不绘制，保持现有透明背景行为。
        val bg = preview.backgroundColor()
        if (bg != null && (bg ushr 24) != 0) {
            bridge.fillRect(box.x(), box.y(), box.width(), box.height(), bg)
        }

        // ui-runtime 渲染层无鼠标坐标；followMouse 由平台层 UiRenderBridge 实现自行读取真实鼠标位置决定朝向，
        // 这里传 NaN 作为「由平台决定」信号。关闭跟随时传预览中心点，让平台层相对偏移为 0，模型保持目视前方。
        val mouseX = if (preview.followMouse()) Float.NaN else box.x() + box.width() / 2f
        val mouseY = if (preview.followMouse()) Float.NaN else box.y() + box.height() / 2f
        bridge.drawEntityPreview(
            preview.source().orEmpty(),
            preview.entityType(),
            preview.entityName(),
            box.x(),
            box.y(),
            box.width(),
            box.height(),
            mouseX,
            mouseY,
            preview.scale(),
            preview.rotationX(),
            preview.rotationY(),
            preview.followMouse(),
        )

        // Task 2a：hover 高亮（在 3D 实体之后合成）。3D 模型 tint 需改 Fabric 签名，延后。
        val hover = preview.hoverOverlayColor()
        if (preview.hovered() && hover != null && (hover ushr 24) != 0) {
            bridge.fillRect(box.x(), box.y(), box.width(), box.height(), hover)
        }
    }
}
